<template>
  <v-row>
    <!-- Subtotal -->
    <v-col cols="6" :md="12/cols">
      <v-card class="px-6 py-lg-8 py-3 subiva-card" :style="cardStyle">
        <div class="font-weight-bold mb-2">Subtotal</div>
        <div :class="!isMobile?'text-h6':'text-h7'">{{ formatCurrency(getValue(['sum_subtotal'])) }}</div>
        <div class="text-caption text-grey-darken-1">
          Sugerido: {{ formatCurrency(getValue(['sum_suggested_subtotal'])) }}
        </div>
      </v-card>
    </v-col>

    <!-- IVA -->
    <v-col cols="6" :md="12/cols">
      <v-card class="px-6 py-lg-8 py-3 subiva-card" :style="cardStyle">
        <div class="font-weight-bold mb-2">IVA</div>
        <div :class="!isMobile?'text-h6':'text-h7'">{{ formatCurrency(getValue(['sum_iva'])) }}</div>
        <div class="text-caption text-grey-darken-1">
          Sugerido: {{ formatCurrency(getValue(['sum_suggested_iva'])) }}
        </div>
      </v-card>
    </v-col>

    <!-- Total -->
    <v-col cols="6" :md="12/cols">
      <v-card class="px-6 py-lg-8 py-3 subiva-card" :style="cardStyle">
        <div class="font-weight-bold mb-2">Total</div>
        <div :class="!isMobile?'text-h6':'text-h7'">{{ formatCurrency(getValue(['sum_total'])) }}</div>
        <div class="text-caption text-grey-darken-1">
          Sugerido: {{ formatCurrency(getValue(['sum_suggested_total'])) }}
        </div>
      </v-card>
    </v-col>

    <!-- Autorizaciones -->
    <v-col cols="6" :md="12/cols">
      <v-card class="px-6 py-lg-8 py-3 subiva-card" :style="authStyle">
        <div class="font-weight-bold mb-2">Necesitan Autorización</div>
        <div :class="!isMobile?'text-h6':'text-h7'">
          {{ getValue(['total_needs_authorization', 'total_ventas_needs_authorization']) }}/
          {{ getValue(['total_quotations', 'ventas']) }}
        </div>
        <div class="text-caption" :style="{opacity: .95}">
          {{ formatCurrency(getValue(['sum_needs_authorization'])) }}
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useDisplay } from 'vuetify';

const { mobile } = useDisplay();
const isMobile = computed(() => mobile.value);

const props = defineProps<{
  records: Record<string, any> | null,
  cols: Record<number, any> | null,
}>();

function getValue(keys: string[]): number {
  if (!props.records) return 0;
  for (const k of keys) {
    const val = (props.records as any)[k];
    if (val !== undefined && val !== null) {
      if (typeof val === 'string') return Number(val) || 0;
      if (typeof val === 'number') return val;
    }
  }
  return 0;
}
function formatCurrency(value: number | undefined | null) {
  if (value == null) return '$0.00';
  return Number(value).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

/* ====== Dark mode ====== */
const isDarkMode = ref(
  typeof window !== 'undefined'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false
);
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', e => (isDarkMode.value = e.matches));
}

/* ====== Branding por hostname ====== */
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isDominiomedios = hostname === 'crm.dominiomedios.com';
const BLUE_BASE = '#2563eb'; // color base para otros clientes

// Helpers HSL
function hexToHsl(hex: string) {
  let r = 0, g = 0, b = 0;
  const c = hex.replace('#','');
  if (c.length === 3) {
    r = parseInt(c[0] + c[0], 16);
    g = parseInt(c[1] + c[1], 16);
    b = parseInt(c[2] + c[2], 16);
  } else {
    r = parseInt(c.substring(0,2), 16);
    g = parseInt(c.substring(2,4), 16);
    b = parseInt(c.substring(4,6), 16);
  }
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b);
  let h = 0, s = 0, l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return { h, s, l };
}
function hslToHex(h: number, s: number, l: number) {
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  const toHex = (x: number) => Math.round(x * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
function cardBgFromBase(baseHex: string, dark = false) {
  const { h, s } = hexToHsl(baseHex);
  // fondo para card: muy claro en light, bastante oscuro pero legible en dark
  const l = dark ? 0.16 : 0.93;
  const sAdj = dark ? Math.min(1, s * 0.75) : Math.max(0.25, s * 0.45);
  return hslToHex(h, sAdj, l);
}
function readableTextColor(bgHex: string) {
  // luminancia rápida: si es oscuro => blanco, si es claro => #111
  const c = bgHex.replace('#','');
  const r = parseInt(c.substring(0,2), 16);
  const g = parseInt(c.substring(2,4), 16);
  const b = parseInt(c.substring(4,6), 16);
  const luminance = (0.299*r + 0.587*g + 0.114*b)/255;
  return luminance < 0.5 ? '#fff' : '#111';
}

/* ====== Estilos de tarjeta ====== */
// Cards normales
/*const cardStyle = computed(() => {
  if (isDominiomedios) {
    // Respeta tu #232223 en dark; y #ececec en light
    const bg = isDarkMode.value ? '#232223' : '#ececec';
    const color = isDarkMode.value ? '#fff' : '#111';
    return `background:${bg}; color:${color};`;
  }
  // Otros clientes: derivado de azul
  const bg = cardBgFromBase(BLUE_BASE, isDarkMode.value);
  const color = readableTextColor(bg);
  return `background:${bg}; color:${color};`;
});*/

// Tarjeta de autorización (badge de marca)
const authStyle = computed(() => {
  if (isDominiomedios) {
    return 'background:#801631; color:#fff; text-align:center;';
  }
  // Para otros clientes usa un acento del azul base (un poco más saturado y medio oscuro)
  const { h, s } = hexToHsl(BLUE_BASE);
  const bg = hslToHex(h, Math.min(1, s * 0.85), 0.35);
  return `background:${bg}; color:#fff; text-align:center;`;
});
</script>

<style scoped>
.subiva-card {
  border-radius: 12px;
  min-height: 100px;
}
.font-weight-bold { font-weight: bold; }
.text-h6 { font-size: 1.6rem; font-weight: 700; }
.text-h7 { font-size: 1rem;  font-weight: 700; }

/* para que los v-slide-group arrows no ocupen ancho extra si algún día usas chips aquí */
.v-slide-group__next, .v-slide-group__prev { min-width: 0 !important; }
</style>

<script lang="ts">
export default { name: 'ReportFilterDate' }
</script>
